export default {
  name: "Place of Employment and Wage Information",
  prefix: "E.c",
  sections: [
    {
      name: "Place of Employment",
      fields: [
        {
          code: "E.c.1",
          machine_name: "ec1_work_address_1",
          widget: "text",
          label: "Worksite Address",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
            {
              maxLength: 60,
              message: "This field cannot be more than 60 characters long.",
            },
          ],
          maxLength: 60,
          tooltip:
            "Enter the street address of the worksite location where work will be performed. The worksite address must be a physical location and cannot be a P.O. Box.",
        },
        {
          code: "E.c.2",
          machine_name: "ec2_work_address_2",
          widget: "text",
          label: "Worksite Address <i>(apartment/suite/floor and number)</i>",
          validate: [
            {
              maxLength: 60,
              message: "This field cannot be more than 60 characters long.",
            },
          ],
          maxLength: 60,
          tooltip:
            "If additional space is needed for the street address, use this line. If no additional space is needed, enter “N/A.”",
        },
        {
          code: "E.c.3",
          machine_name: "ec3_work_city",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
            {
              maxLength: 30,
              message: "This field cannot be more than 30 characters long.",
            },
          ],
          maxLength: 30,
          tooltip: `Enter the city of the worksite location.`,
        },
        {
          code: "E.c.4",
          machine_name: "ec4_work_state_id",
          widget: "select",
          label: "State",
          placeholder: ["NORTHERN MARIANA ISLANDS"],
          selection: ["NORTHERN MARIANA ISLANDS"],
          disabled: true,
          validate: ["required"],
          tooltip: `Enter the State, District, or Territory of the worksite location.`,
        },
        {
          code: "E.c.5",
          machine_name: "ec5_work_zip",
          widget: "text",
          label: "Postal Code",
          validate: [
            "required",
            { postalCompare: ["96950", "96951", "96952"] },
            "noNAs",
          ],
          maxLength: 10,
          tooltip: `Enter the postal (zip) code of the worksite location.`,
        },
      ],
    },
    {
      name: "Wage Information",
      fields: [
        {
          isGroup: {
            code: "E.c.6",
            label: "Basic Wage Rate Paid",
            tooltip: `Enter the basic rate of pay to be paid for the period of employment at this worksite location, if applicable. If the wage offer is expressed as a range, enter the bottom of the wage range to be paid on the “From” line and enter the top of the wage range on the “To” line.`,
            fields: [
              {
                code: "",
                machine_name: "ec6_basic_rate_from",
                widget: "wage",
                label: "From",
                validate: ["required"],
                maxLength: 4,
              },
              {
                code: "",
                machine_name: "ec6_basic_rate_to",
                widget: "wage",
                label: "To",
                validate: [
                  {
                    fieldComparison: "ec6_basic_rate_to",
                    message:
                      '"To" wage must be greater than or equal to "From" wage',
                    needsFieldValues: true,
                    extraData: {
                      operator: ">=",
                      valueOf: "ec6_basic_rate_from",
                    },
                  },
                ],
                maxLength: 4,
              },
            ],
          },
        },
        {
          isGroup: {
            label: "Overtime Wage Rate Paid",
            code: "E.c.6a",
            tooltip: `Enter the rate of overtime pay, if applicable, to be paid to worker(s). If the wage offer is expressed as a range, enter the bottom of the wage range to be paid on the “From” line and enter the top of the wage range on the “To” line. If no overtime is offered, leave this section blank.`,
            fields: [
              {
                machine_name: "ec6a_overtime_rate_from",
                widget: "wage",
                label: "From",
                // validate: ["required"],
                maxLength: 4,
                warnings: [
                  {
                    text: "Overtime is paid at 1.5 times the wage rate offered.",
                    isVisible: {
                      when: "ec6a_overtime_rate_from",
                      is: ["ec6_basic_rate_to"],
                      operator: "overtimeComparison",
                    },
                  },
                ],
              },
              {
                machine_name: "ec6a_overtime_rate_to",
                widget: "wage",
                label: "To",
                validate: [
                  {
                    fieldComparison: "ec6a_overtime_rate_to",
                    message:
                      '"To" wage must be greater than or equal to "From" wage',
                    needsFieldValues: true,
                    extraData: {
                      operator: ">=",
                      valueOf: "ec6a_overtime_rate_from",
                    },
                  },
                ],
                maxLength: 4,
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.c.7",
            machine_name: "ec7_pay_rate_id",
            label: "Per <i>(Choose only one)</i>",
            tooltip:
              "Indicate whether the rate of pay is per hour, week, bi-weekly, or “Other” (e.g., month, year) by selecting the corresponding box.  Make only one selection.",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Hour",
                value: "Hour",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Week",
                value: "Week",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Bi-Week",
                value: "Bi-Week",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Month",
                value: "Month",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Year",
                value: "Year",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Piece Rate",
                value: "Piece Rate",
                validate: ["required"],
              },
            ],
          },
        },
        {
          code: "E.c.7a",
          machine_name: "ec7a_pay_comments",
          widget: "text",
          label: "Additional conditions about the wage rate to be paid",
          maxLength: 120,
          tooltip:
            "Briefly describe any conditions about the wage rate to be paid.  For example, if the answer to question 7 is “Piece Rate,” enter the units that govern how the piece rate is paid (e.g., 5/8 bushel, 90 pound bag or box, 10 box bin).  Please also describe here any bonuses, fringe benefits, subsidized housing or meals, or any other benefits associated with this job opportunity.  Enter “N/A” or “None” if there are no additional conditions about the wage rate to be paid.",
        },
        {
          isGroup: {
            code: "E.c.8",
            machine_name: "ec8_pay_frequency_id",
            label: "Frequency of Pay",
            tooltip:
              "Enter whether wages are paid daily, weekly, every two weeks, or over a different period. Mark only one box",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Daily",
                value: "Daily",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Weekly",
                value: "Weekly",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Bi-Weekly",
                value: "Bi-Weekly",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Other",
                value: "Other",
                validate: ["required"],
              },
            ],
          },
        },
        {
          machine_name: "ec8_pay_frequency_other",
          label: "Other frequency of pay",
          widget: "text",
          validate: ["required"],
          maxLength: 35,
          isVisible: {
            when: "ec8_pay_frequency_id",
            is: "Other",
          },
        },
      ],
    },
    {
      fields: [
        {
          isGroup: {
            code: "E.c.9",
            label:
              "Will work be performed at worksite locations other than the one identified above?",
            machine_name: "ec9_is_multiple_locations",
            tooltip:
              "If work will <u>not</u> be performed in location(s) other than the address listed in questions E.c.1-E.c.5, mark “No.",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
                setSchema: [
                  {
                    set: "appx_b",
                    to: [],
                  },
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "E.c.10",
            machine_name: "ec10_appx_b_attached",
            widget: "radio",
            label:
              'If "Yes" is marked in question E.c.9, a completed Appendix B is attached to this application.',
            tooltip:
              "If “Yes,” is marked in E.c.9, a completed <i>Appendix B</i> must be completed.  Identify the geographic place(s) of employment with as much specificity as possible, such as the city, state, and postal code where work will be performed.  The employer must provide enough geographic detail to cover all the worksite locations of intended employment.",
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
              },
            ],
            isVisible: {
              when: "ec9_is_multiple_locations",
              is: "YES",
            },
          },
        },
      ],
    },
  ],
};
